/**
 * Constitutive Board Wizard
 *
 * A 3-step wizard for creating constitutive board meeting protocols
 * (konstituerande styrelseprotokoll) according to ABL.
 *
 * This meeting is typically held immediately after an annual meeting where
 * a new board has been elected.
 *
 * Steps:
 * 1. Meeting Details - Date, time, place, attendees (board members)
 * 2. Decisions - Chairman, signature rights, attest rules, CEO, bank authorization
 * 3. Review & Save
 */

import { useState, useEffect, useCallback } from "react";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  FileText,
  ChevronRight,
  ChevronLeft,
  Check,
  Loader2,
  AlertCircle,
  UserCheck,
  PenTool,
  Building2,
  Banknote,
  CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/common/Modal";
import {
  protocolsApi,
  type ConstitutiveBoardPrefillData,
  type ConstitutiveBoardDecisions,
  type CreateProtocolData,
  type AnnualMeetingWithBoardElection,
} from "@/lib/api";
import { formatTime } from "@/lib/utils";

interface ConstitutiveBoardWizardProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  annualMeetingId?: string; // Pre-selected annual meeting to create constitutive from
  onSuccess?: (protocolId: string) => void;
}

type WizardStep = 1 | 2 | 3;

interface MeetingDetails {
  meetingDate: string;
  meetingTime: string;
  meetingPlace: string;
  secretaryName: string;
}

interface BoardMemberAttendee {
  name: string;
  role: string;
  present: boolean;
}

export function ConstitutiveBoardWizard({
  isOpen,
  onClose,
  companyId,
  annualMeetingId,
  onSuccess,
}: ConstitutiveBoardWizardProps) {
  const [step, setStep] = useState<WizardStep>(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [prefillData, setPrefillData] = useState<ConstitutiveBoardPrefillData | null>(null);

  // Available annual meetings with board election (for selection)
  const [availableAnnualMeetings, setAvailableAnnualMeetings] = useState<AnnualMeetingWithBoardElection[]>([]);
  const [selectedAnnualMeetingId, setSelectedAnnualMeetingId] = useState<string | undefined>(annualMeetingId);

  // Step 1: Meeting Details
  const [meetingDetails, setMeetingDetails] = useState<MeetingDetails>({
    meetingDate: new Date().toISOString().split("T")[0],
    meetingTime: "",
    meetingPlace: "",
    secretaryName: "",
  });

  // Board member attendees
  const [attendees, setAttendees] = useState<BoardMemberAttendee[]>([]);

  // Step 2: Decisions
  const [decisions, setDecisions] = useState<ConstitutiveBoardDecisions>({
    chairpersonElection: { name: "" },
    signatureRights: { type: "board_together" },
    attestRules: { enabled: false },
    ceoAppointment: { appointed: false },
    bankAuthorization: { enabled: false },
    nextMeeting: { scheduled: false },
  });

  // Load available annual meetings
  useEffect(() => {
    if (!isOpen) return;

    const loadAvailableAnnualMeetings = async () => {
      try {
        const response = await protocolsApi.getAnnualMeetingsWithBoardElection(companyId);
        if (response.success && response.data) {
          setAvailableAnnualMeetings(response.data.annualMeetings);

          // Auto-select first if no pre-selection
          if (!selectedAnnualMeetingId && response.data.annualMeetings.length > 0) {
            setSelectedAnnualMeetingId(response.data.annualMeetings[0].id);
          }
        }
      } catch (error) {
        console.error("Failed to load annual meetings:", error);
      }
    };

    loadAvailableAnnualMeetings();
  }, [isOpen, companyId, selectedAnnualMeetingId]);

  // Load prefill data when annual meeting is selected
  useEffect(() => {
    if (!isOpen || !selectedAnnualMeetingId) return;

    const loadPrefillData = async () => {
      setIsLoading(true);
      try {
        const response = await protocolsApi.getConstitutivePrefill(companyId, selectedAnnualMeetingId);
        if (response.success && response.data) {
          setPrefillData(response.data);

          // Pre-fill meeting details
          setMeetingDetails({
            meetingDate: response.data.annualMeeting.meetingDate,
            meetingTime: "",
            meetingPlace: response.data.annualMeeting.meetingPlace || response.data.company.seatMunicipality || "",
            secretaryName: "",
          });

          // Pre-fill attendees from board members
          const boardAttendees: BoardMemberAttendee[] = response.data.boardMembers.map((m) => ({
            name: m.name,
            role: m.role,
            present: true,
          }));
          setAttendees(boardAttendees);

          // Pre-fill chairman if suggested
          if (response.data.suggestedChairperson) {
            setDecisions((prev) => ({
              ...prev,
              chairpersonElection: { name: response.data!.suggestedChairperson! },
            }));
          }
        }
      } catch (error) {
        console.error("Failed to load prefill data:", error);
        toast.error("Kunde inte ladda data");
      } finally {
        setIsLoading(false);
      }
    };

    loadPrefillData();
  }, [isOpen, companyId, selectedAnnualMeetingId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setSelectedAnnualMeetingId(annualMeetingId);
      setMeetingDetails({
        meetingDate: new Date().toISOString().split("T")[0],
        meetingTime: "",
        meetingPlace: "",
        secretaryName: "",
      });
      setAttendees([]);
      setDecisions({
        chairpersonElection: { name: "" },
        signatureRights: { type: "board_together" },
        attestRules: { enabled: false },
        ceoAppointment: { appointed: false },
        bankAuthorization: { enabled: false },
        nextMeeting: { scheduled: false },
      });
    }
  }, [isOpen, annualMeetingId]);

  const validateStep1 = useCallback((): boolean => {
    if (!selectedAnnualMeetingId) {
      toast.error("Välj en årsstämma att koppla till");
      return false;
    }
    if (!meetingDetails.meetingDate) {
      toast.error("Ange mötesdatum");
      return false;
    }
    const presentAttendees = attendees.filter((a) => a.present);
    if (presentAttendees.length === 0) {
      toast.error("Minst en styrelseledamot måste vara närvarande");
      return false;
    }
    return true;
  }, [meetingDetails, selectedAnnualMeetingId, attendees]);

  const validateStep2 = useCallback((): boolean => {
    if (!decisions.chairpersonElection.name.trim()) {
      toast.error("Ange namn på vald ordförande");
      return false;
    }
    if (decisions.signatureRights.type === "single" && !decisions.signatureRights.singlePerson?.trim()) {
      toast.error("Ange vem som tecknar firman ensam");
      return false;
    }
    if (decisions.signatureRights.type === "two_together") {
      if (!decisions.signatureRights.twoPeople?.person1?.trim() || !decisions.signatureRights.twoPeople?.person2?.trim()) {
        toast.error("Ange båda personerna som tecknar firman i förening");
        return false;
      }
    }
    if (decisions.signatureRights.type === "custom" && !decisions.signatureRights.customText?.trim()) {
      toast.error("Ange firmateckningsrätten i fritext");
      return false;
    }
    if (decisions.ceoAppointment?.appointed && !decisions.ceoAppointment.name?.trim()) {
      toast.error("Ange VD:s namn");
      return false;
    }
    return true;
  }, [decisions]);

  const handleNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step < 3) setStep((step + 1) as WizardStep);
  };

  const handleBack = () => {
    if (step > 1) setStep((step - 1) as WizardStep);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const data: CreateProtocolData = {
        linkedAnnualMeetingId: selectedAnnualMeetingId,
        type: "constitutive_board",
        meetingDate: meetingDetails.meetingDate,
        meetingTime: meetingDetails.meetingTime || undefined,
        meetingPlace: meetingDetails.meetingPlace || undefined,
        chairpersonName: decisions.chairpersonElection.name,
        secretaryName: meetingDetails.secretaryName || undefined,
        attendees: attendees.filter((a) => a.present).map((a) => ({
          name: a.name,
          shares: 0,
          votes: 0,
          present: true,
        })),
        decisions: decisions,
      };

      const response = await protocolsApi.create(companyId, data);

      if (response.success && response.data) {
        toast.success("Konstituerande protokoll skapat!");
        onSuccess?.(response.data.protocol.id);
        onClose();
      } else {
        toast.error(response.error?.message || "Kunde inte skapa protokoll");
      }
    } catch (error) {
      console.error("Failed to create protocol:", error);
      toast.error("Ett fel uppstod");
    } finally {
      setIsSaving(false);
    }
  };

  const stepTitles: Record<WizardStep, string> = {
    1: "Mötesuppgifter",
    2: "Beslut",
    3: "Granska",
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-primary-600" />
          <span>Konstituerande styrelseprotokoll</span>
        </div>
      }
      size="2xl"
    >
      {isLoading && selectedAnnualMeetingId ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    s === step
                      ? "bg-primary-600 text-white"
                      : s < step
                        ? "bg-green-500 text-white"
                        : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {s < step ? <Check className="w-4 h-4" /> : s}
                </div>
                <span
                  className={`ml-2 text-sm font-medium ${
                    s === step ? "text-gray-900" : "text-gray-500"
                  }`}
                >
                  {stepTitles[s as WizardStep]}
                </span>
                {s < 3 && (
                  <ChevronRight
                    className={`w-5 h-5 mx-4 ${s < step ? "text-green-500" : "text-gray-300"}`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Company Info */}
          {prefillData && (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-900">{prefillData.company.name}</p>
              <p className="text-xs text-gray-500">Org.nr: {prefillData.company.organizationNumber}</p>
            </div>
          )}

          {/* Step Content */}
          <div className="min-h-[350px]">
            {step === 1 && (
              <Step1MeetingDetails
                details={meetingDetails}
                onChange={setMeetingDetails}
                attendees={attendees}
                onAttendeesChange={setAttendees}
                prefillData={prefillData}
                availableAnnualMeetings={availableAnnualMeetings}
                selectedAnnualMeetingId={selectedAnnualMeetingId}
                onAnnualMeetingChange={setSelectedAnnualMeetingId}
              />
            )}
            {step === 2 && (
              <Step2Decisions
                decisions={decisions}
                onChange={setDecisions}
                boardMembers={attendees.filter((a) => a.present)}
              />
            )}
            {step === 3 && (
              <Step3Review
                meetingDetails={meetingDetails}
                attendees={attendees}
                decisions={decisions}
                prefillData={prefillData}
              />
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={step === 1 ? onClose : handleBack}
              className="btn btn-secondary"
              disabled={isSaving}
            >
              {step === 1 ? "Avbryt" : (
                <>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Tillbaka
                </>
              )}
            </button>

            {step < 3 ? (
              <button type="button" onClick={handleNext} className="btn btn-primary">
                Fortsätt
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="btn btn-primary flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sparar...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Skapa protokoll
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Step 1: Meeting Details
// ═══════════════════════════════════════════════════════════════════

interface Step1Props {
  details: MeetingDetails;
  onChange: (details: MeetingDetails) => void;
  attendees: BoardMemberAttendee[];
  onAttendeesChange: (attendees: BoardMemberAttendee[]) => void;
  prefillData: ConstitutiveBoardPrefillData | null;
  availableAnnualMeetings: AnnualMeetingWithBoardElection[];
  selectedAnnualMeetingId?: string;
  onAnnualMeetingChange: (id: string) => void;
}

function Step1MeetingDetails({
  details,
  onChange,
  attendees,
  onAttendeesChange,
  prefillData: _prefillData,
  availableAnnualMeetings,
  selectedAnnualMeetingId,
  onAnnualMeetingChange,
}: Step1Props) {
  const updateField = (field: keyof MeetingDetails, value: string) => {
    onChange({ ...details, [field]: value });
  };

  const toggleAttendeePresent = (index: number) => {
    const updated = [...attendees];
    updated[index] = { ...updated[index], present: !updated[index].present };
    onAttendeesChange(updated);
  };

  const formatFiscalYear = (start: string, end: string) => {
    return `${start} – ${end}`;
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600">
        Ange mötesuppgifter för det konstituerande styrelsemötet. Mötet hålls typiskt direkt efter
        årsstämman där ny styrelse valdes.
      </p>

      {/* Annual Meeting Selection */}
      <div className="p-4 bg-primary-50 rounded-lg border border-primary-200">
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          <FileText className="w-4 h-4 inline mr-1" />
          Kopplad årsstämma *
        </label>
        {availableAnnualMeetings.length > 0 ? (
          <select
            value={selectedAnnualMeetingId || ""}
            onChange={(e) => onAnnualMeetingChange(e.target.value)}
            className="w-full px-3 py-2 border border-primary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
          >
            <option value="">Välj årsstämma...</option>
            {availableAnnualMeetings.map((m) => (
              <option key={m.id} value={m.id}>
                Årsstämma {m.meetingDate} (RÅ: {formatFiscalYear(m.fiscalYearStart, m.fiscalYearEnd)})
              </option>
            ))}
          </select>
        ) : (
          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Ingen tillgänglig årsstämma</p>
                <p className="text-xs mt-1">
                  Det finns inga årsstämmor med styrelseval att koppla till. Skapa först ett
                  årsstämmoprotokoll med styrelseval.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Date and Time */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Calendar className="w-4 h-4 inline mr-1" />
            Datum *
          </label>
          <input
            type="date"
            value={details.meetingDate}
            onChange={(e) => updateField("meetingDate", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Clock className="w-4 h-4 inline mr-1" />
            Tid
          </label>
          <input
            type="time"
            value={details.meetingTime}
            onChange={(e) => updateField("meetingTime", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Place */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <MapPin className="w-4 h-4 inline mr-1" />
          Plats
        </label>
        <input
          type="text"
          value={details.meetingPlace}
          onChange={(e) => updateField("meetingPlace", e.target.value)}
          placeholder="T.ex. Bolagets kontor, Stockholm"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Secretary */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Sekreterare/protokollförare
        </label>
        <input
          type="text"
          value={details.secretaryName}
          onChange={(e) => updateField("secretaryName", e.target.value)}
          placeholder="Namn på protokollförare (valfritt)"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Board Members */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Users className="w-4 h-4" />
          Närvarande styrelseledamöter
        </h4>

        {attendees.length === 0 ? (
          <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Välj en årsstämma för att ladda styrelseledamöter</p>
          </div>
        ) : (
          <div className="space-y-2">
            {attendees.map((attendee, index) => (
              <label
                key={index}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  attendee.present
                    ? "bg-green-50 border-green-200"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <input
                  type="checkbox"
                  checked={attendee.present}
                  onChange={() => toggleAttendeePresent(index)}
                  className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{attendee.name}</p>
                  <p className="text-xs text-gray-500">{attendee.role}</p>
                </div>
                {attendee.present && (
                  <Check className="w-4 h-4 text-green-600" />
                )}
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Step 2: Decisions
// ═══════════════════════════════════════════════════════════════════

interface Step2Props {
  decisions: ConstitutiveBoardDecisions;
  onChange: (decisions: ConstitutiveBoardDecisions) => void;
  boardMembers: BoardMemberAttendee[];
}

function Step2Decisions({ decisions, onChange, boardMembers }: Step2Props) {
  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600">
        Ange de beslut som fattades på det konstituerande styrelsemötet.
      </p>

      {/* Chairman Election */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-primary-600" />
          § 3 Val av ordförande *
        </h4>
        {boardMembers.length > 0 && (
          <select
            value={decisions.chairpersonElection.name}
            onChange={(e) =>
              onChange({
                ...decisions,
                chairpersonElection: { name: e.target.value },
              })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white mb-2"
          >
            <option value="">Välj styrelseledamot...</option>
            {boardMembers.map((m) => (
              <option key={m.name} value={m.name}>
                {m.name} ({m.role})
              </option>
            ))}
          </select>
        )}
        <input
          type="text"
          value={decisions.chairpersonElection.name}
          onChange={(e) =>
            onChange({
              ...decisions,
              chairpersonElection: { name: e.target.value },
            })
          }
          placeholder={boardMembers.length > 0 ? "Eller skriv in annat namn" : "Namn på ordförande"}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Signature Rights */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <PenTool className="w-4 h-4 text-primary-600" />
          § 4 Firmateckning *
        </h4>
        <div className="space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="signatureRights"
              checked={decisions.signatureRights.type === "board_together"}
              onChange={() =>
                onChange({
                  ...decisions,
                  signatureRights: { type: "board_together" },
                })
              }
              className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Styrelsen i förening</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="signatureRights"
              checked={decisions.signatureRights.type === "single"}
              onChange={() =>
                onChange({
                  ...decisions,
                  signatureRights: { type: "single", singlePerson: "" },
                })
              }
              className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">En person ensam</span>
          </label>
          {decisions.signatureRights.type === "single" && (
            <div className="ml-6">
              <input
                type="text"
                value={decisions.signatureRights.singlePerson || ""}
                onChange={(e) =>
                  onChange({
                    ...decisions,
                    signatureRights: {
                      ...decisions.signatureRights,
                      singlePerson: e.target.value,
                    },
                  })
                }
                placeholder="Namn på firmatecknare"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          )}

          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="signatureRights"
              checked={decisions.signatureRights.type === "two_together"}
              onChange={() =>
                onChange({
                  ...decisions,
                  signatureRights: { type: "two_together", twoPeople: { person1: "", person2: "" } },
                })
              }
              className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Två i förening</span>
          </label>
          {decisions.signatureRights.type === "two_together" && (
            <div className="ml-6 grid grid-cols-2 gap-2">
              <input
                type="text"
                value={decisions.signatureRights.twoPeople?.person1 || ""}
                onChange={(e) =>
                  onChange({
                    ...decisions,
                    signatureRights: {
                      ...decisions.signatureRights,
                      twoPeople: {
                        person1: e.target.value,
                        person2: decisions.signatureRights.twoPeople?.person2 || "",
                      },
                    },
                  })
                }
                placeholder="Person 1"
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <input
                type="text"
                value={decisions.signatureRights.twoPeople?.person2 || ""}
                onChange={(e) =>
                  onChange({
                    ...decisions,
                    signatureRights: {
                      ...decisions.signatureRights,
                      twoPeople: {
                        person1: decisions.signatureRights.twoPeople?.person1 || "",
                        person2: e.target.value,
                      },
                    },
                  })
                }
                placeholder="Person 2"
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          )}

          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="signatureRights"
              checked={decisions.signatureRights.type === "custom"}
              onChange={() =>
                onChange({
                  ...decisions,
                  signatureRights: { type: "custom", customText: "" },
                })
              }
              className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Annan firmateckningsrätt</span>
          </label>
          {decisions.signatureRights.type === "custom" && (
            <div className="ml-6">
              <textarea
                value={decisions.signatureRights.customText || ""}
                onChange={(e) =>
                  onChange({
                    ...decisions,
                    signatureRights: {
                      ...decisions.signatureRights,
                      customText: e.target.value,
                    },
                  })
                }
                placeholder="Beskriv firmateckningsrätten..."
                rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          )}
        </div>
      </div>

      {/* Attest Rules (Optional) */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <label className="flex items-center gap-3 mb-3">
          <input
            type="checkbox"
            checked={decisions.attestRules?.enabled || false}
            onChange={(e) =>
              onChange({
                ...decisions,
                attestRules: { enabled: e.target.checked },
              })
            }
            className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
          />
          <span className="font-medium text-gray-900">§ 5 Attestregler</span>
          <span className="text-xs text-gray-500">(valfritt)</span>
        </label>

        {decisions.attestRules?.enabled && (
          <div className="ml-7 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Beloppsgräns (kr)</label>
                <input
                  type="number"
                  value={decisions.attestRules?.lowLimit || ""}
                  onChange={(e) =>
                    onChange({
                      ...decisions,
                      attestRules: {
                        ...decisions.attestRules!,
                        lowLimit: parseInt(e.target.value) || undefined,
                      },
                    })
                  }
                  placeholder="T.ex. 25000"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Attestant under gräns</label>
                <input
                  type="text"
                  value={decisions.attestRules?.lowAttester || ""}
                  onChange={(e) =>
                    onChange({
                      ...decisions,
                      attestRules: {
                        ...decisions.attestRules!,
                        lowAttester: e.target.value,
                      },
                    })
                  }
                  placeholder="Namn"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Attestant över gräns</label>
              <input
                type="text"
                value={decisions.attestRules?.highAttester || ""}
                onChange={(e) =>
                  onChange({
                    ...decisions,
                    attestRules: {
                      ...decisions.attestRules!,
                      highAttester: e.target.value,
                    },
                  })
                }
                placeholder="Namn"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* CEO Appointment (Optional) */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <label className="flex items-center gap-3 mb-3">
          <input
            type="checkbox"
            checked={decisions.ceoAppointment?.appointed || false}
            onChange={(e) =>
              onChange({
                ...decisions,
                ceoAppointment: { appointed: e.target.checked },
              })
            }
            className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
          />
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-primary-600" />
            <span className="font-medium text-gray-900">§ 6 VD-tillsättning</span>
          </div>
          <span className="text-xs text-gray-500">(valfritt)</span>
        </label>

        {decisions.ceoAppointment?.appointed && (
          <div className="ml-7">
            <input
              type="text"
              value={decisions.ceoAppointment?.name || ""}
              onChange={(e) =>
                onChange({
                  ...decisions,
                  ceoAppointment: {
                    ...decisions.ceoAppointment!,
                    name: e.target.value,
                  },
                })
              }
              placeholder="VD:s namn"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        )}
      </div>

      {/* Bank Authorization (Optional) */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <label className="flex items-center gap-3 mb-3">
          <input
            type="checkbox"
            checked={decisions.bankAuthorization?.enabled || false}
            onChange={(e) =>
              onChange({
                ...decisions,
                bankAuthorization: { enabled: e.target.checked },
              })
            }
            className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
          />
          <div className="flex items-center gap-2">
            <Banknote className="w-4 h-4 text-primary-600" />
            <span className="font-medium text-gray-900">§ 7 Bankbehörighet</span>
          </div>
          <span className="text-xs text-gray-500">(valfritt)</span>
        </label>

        {decisions.bankAuthorization?.enabled && (
          <div className="ml-7 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Behörig person</label>
              <input
                type="text"
                value={decisions.bankAuthorization?.authorizedPerson || ""}
                onChange={(e) =>
                  onChange({
                    ...decisions,
                    bankAuthorization: {
                      ...decisions.bankAuthorization!,
                      authorizedPerson: e.target.value,
                    },
                  })
                }
                placeholder="Namn"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Bank</label>
              <input
                type="text"
                value={decisions.bankAuthorization?.bank || ""}
                onChange={(e) =>
                  onChange({
                    ...decisions,
                    bankAuthorization: {
                      ...decisions.bankAuthorization!,
                      bank: e.target.value,
                    },
                  })
                }
                placeholder="T.ex. Nordea"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Next Meeting (Optional) */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <label className="flex items-center gap-3 mb-3">
          <input
            type="checkbox"
            checked={decisions.nextMeeting?.scheduled || false}
            onChange={(e) =>
              onChange({
                ...decisions,
                nextMeeting: { scheduled: e.target.checked },
              })
            }
            className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
          />
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-primary-600" />
            <span className="font-medium text-gray-900">§ 8 Nästa styrelsemöte</span>
          </div>
          <span className="text-xs text-gray-500">(valfritt)</span>
        </label>

        {decisions.nextMeeting?.scheduled && (
          <div className="ml-7">
            <input
              type="date"
              value={decisions.nextMeeting?.date || ""}
              onChange={(e) =>
                onChange({
                  ...decisions,
                  nextMeeting: {
                    ...decisions.nextMeeting!,
                    date: e.target.value,
                  },
                })
              }
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Step 3: Review
// ═══════════════════════════════════════════════════════════════════

interface Step3Props {
  meetingDetails: MeetingDetails;
  attendees: BoardMemberAttendee[];
  decisions: ConstitutiveBoardDecisions;
  prefillData: ConstitutiveBoardPrefillData | null;
}

function Step3Review({ meetingDetails, attendees, decisions, prefillData }: Step3Props) {
  const presentAttendees = attendees.filter((a) => a.present);

  const getSignatureRightsText = (): string => {
    switch (decisions.signatureRights.type) {
      case "board_together":
        return "Styrelsen i förening";
      case "single":
        return `${decisions.signatureRights.singlePerson} ensam`;
      case "two_together":
        return `${decisions.signatureRights.twoPeople?.person1} och ${decisions.signatureRights.twoPeople?.person2} i förening`;
      case "custom":
        return decisions.signatureRights.customText || "";
      default:
        return "";
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Granska uppgifterna innan du skapar protokollet.
      </p>

      {/* Meeting Info */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-3">Mötesuppgifter</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Bolag:</span>
            <span className="font-medium">{prefillData?.company.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Datum:</span>
            <span className="font-medium">{meetingDetails.meetingDate}</span>
          </div>
          {meetingDetails.meetingTime && (
            <div className="flex justify-between">
              <span className="text-gray-600">Tid:</span>
              <span className="font-medium">{formatTime(meetingDetails.meetingTime)}</span>
            </div>
          )}
          {meetingDetails.meetingPlace && (
            <div className="flex justify-between">
              <span className="text-gray-600">Plats:</span>
              <span className="font-medium">{meetingDetails.meetingPlace}</span>
            </div>
          )}
        </div>
      </div>

      {/* Attendees */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-3">Närvarande ({presentAttendees.length} st)</h4>
        <div className="space-y-1">
          {presentAttendees.map((a, i) => (
            <p key={i} className="text-sm text-gray-700">
              {a.name} <span className="text-gray-500">({a.role})</span>
            </p>
          ))}
        </div>
      </div>

      {/* Decisions */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-3">Beslut</h4>
        <div className="space-y-3 text-sm">
          <div>
            <span className="text-gray-600">§ 3 Ordförande:</span>
            <span className="ml-2 font-medium">{decisions.chairpersonElection.name}</span>
          </div>
          <div>
            <span className="text-gray-600">§ 4 Firmateckning:</span>
            <span className="ml-2 font-medium">{getSignatureRightsText()}</span>
          </div>
          {decisions.attestRules?.enabled && (
            <div>
              <span className="text-gray-600">§ 5 Attestregler:</span>
              <span className="ml-2 font-medium">
                {decisions.attestRules.lowAttester} upp till{" "}
                {decisions.attestRules.lowLimit?.toLocaleString("sv-SE")} kr,{" "}
                {decisions.attestRules.highAttester} däröver
              </span>
            </div>
          )}
          {decisions.ceoAppointment?.appointed && (
            <div>
              <span className="text-gray-600">§ 6 VD:</span>
              <span className="ml-2 font-medium">{decisions.ceoAppointment.name}</span>
            </div>
          )}
          {decisions.bankAuthorization?.enabled && (
            <div>
              <span className="text-gray-600">§ 7 Bankbehörighet:</span>
              <span className="ml-2 font-medium">
                {decisions.bankAuthorization.authorizedPerson}
                {decisions.bankAuthorization.bank && ` (${decisions.bankAuthorization.bank})`}
              </span>
            </div>
          )}
          {decisions.nextMeeting?.scheduled && (
            <div>
              <span className="text-gray-600">§ 8 Nästa möte:</span>
              <span className="ml-2 font-medium">{decisions.nextMeeting.date}</span>
            </div>
          )}
        </div>
      </div>

      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-800">
          <AlertCircle className="w-4 h-4 inline mr-1" />
          Efter att protokollet skapats kan du ladda ner det som PDF.
        </p>
      </div>
    </div>
  );
}

export default ConstitutiveBoardWizard;
